import { useEffect, useRef } from "react";

/**
 * Device Data Collection (Paso 2) · Sprint 59 · F1B.
 *
 * iframe OCULTO 10x10 que hace POST del accessToken (JWT) a la
 * deviceDataCollectionUrl. Sólo acepta `postMessage` cuando:
 *   · event.origin coincide EXACTAMENTE con `expectedOrigin` (el origen exacto
 *     extraído de la URL confiable recibida desde Setup, ya validada contra la
 *     lista oficial de hosts · nunca una regla wildcard); y
 *   · event.source === iframe.contentWindow (el marco que realmente cargó).
 * Ignora cualquier otro mensaje. Aplica timeout (fallback documentado:
 * proceder a Enrollment igualmente). No hace doble envío. No registra el
 * accessToken. El aviso de fin es sólo una señal de flujo; la autenticación
 * real la determina Enrollment.
 */

export const DeviceDataCollection = ({
  url,
  accessToken,
  expectedOrigin,
  timeoutMs = 10000,
  onComplete,
  onTimeout,
}) => {
  const iframeRef = useRef(null);
  const formRef = useRef(null);
  const enviadoRef = useRef(false);
  const finalizadoRef = useRef(false);

  useEffect(() => {
    if (!url || !accessToken || !expectedOrigin) return undefined;

    const terminar = (via) => {
      if (finalizadoRef.current) return;
      finalizadoRef.current = true;
      window.removeEventListener("message", onMessage);
      if (timer) clearTimeout(timer);
      if (via === "timeout") onTimeout?.();
      else onComplete?.();
    };

    const onMessage = (event) => {
      // Origen EXACTO + fuente = el iframe que cargó la DDC.
      if (event.origin !== expectedOrigin) return;
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) return;
      terminar("message");
    };

    window.addEventListener("message", onMessage);
    let timer = setTimeout(() => terminar("timeout"), timeoutMs);

    if (!enviadoRef.current && formRef.current) {
      enviadoRef.current = true;
      try {
        formRef.current.submit();
      } catch {
        /* jsdom / cross-origin: el submit real ocurre en el navegador. */
      }
    }

    return () => {
      window.removeEventListener("message", onMessage);
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, accessToken, expectedOrigin]);

  return (
    <div data-testid="ddc-container" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }} aria-hidden="true">
      <iframe
        title="cardinal-ddc"
        name="cs-ddc-iframe"
        ref={iframeRef}
        data-testid="ddc-iframe"
        width="10"
        height="10"
        style={{ border: 0 }}
      />
      <form ref={formRef} data-testid="ddc-form" method="POST" action={url} target="cs-ddc-iframe">
        <input type="hidden" name="JWT" value={accessToken} readOnly />
      </form>
    </div>
  );
};

export default DeviceDataCollection;
